import { useState } from 'react';
import { BodyText, Div, DropdownMultiselect, Gutter, Input, ModalDialog } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { UserGroupResponse, UserRole } from '@cdaas/api-clients/dist/asset-admin-openapi';

import styles from './CreateGroupModalDialog.module.scss';

export default function CreateGroupModalDialog({
  isOpen,
  onCancel,
  onCreate,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onCreate: (newGroup: UserGroupResponse) => void;
}) {
  const { t } = useTranslation('organizationForm');
  const tGlobal = useTranslation('global').t;

  const [name, setName] = useState('');
  const [default_user_roles, setDefaultUserRoles] = useState<UserRole[]>([UserRole.Admin]);

  const [groupNameError, setGroupNameError] = useState();

  const reset = () => {
    setName('');
    setDefaultUserRoles([UserRole.Admin]);
  };

  const validate = () => {
    let valid = true;
    if (!name) {
      setGroupNameError(t('createGroupModalDialog.groupNameInputRequiredError'));
      valid = false;
    } else {
      setGroupNameError(undefined);
    }

    return valid;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setGroupNameError(undefined);
  };

  const handleRolesChange = (selectedRole: UserRole) => {
    const roleIndex = default_user_roles.findIndex(role => role === selectedRole);
    setDefaultUserRoles(previousRoles =>
      roleIndex === -1 ? [...previousRoles, selectedRole] : [...previousRoles.slice(0, roleIndex), ...previousRoles.slice(roleIndex + 1)],
    );
  };

  const handleCreate = () => {
    if (validate()) {
      const newUserGroup: UserGroupResponse = {
        name,
        default_user_roles,
        approval_chain: [],
        default_billing_address: null,
        default_billing_contact: null,
        default_delivery_address: null,
        default_delivery_contact: null,
        id: '',
        invoicing_account: null,
        inherited_invoicing_account: {
          name: '',
        },
        policies: {
          lease_procurement_limit: null,
        },
      };
      onCreate(newUserGroup);
      reset();
    }
  };

  const handleCancel = () => {
    onCancel();
    reset();
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      title={t('createGroupModalDialog.title')}
      acceptButtonText={t('createGroupModalDialog.acceptButton')}
      closeButtonText={t('createGroupModalDialog.closeButton')}
      onCloseClick={handleCancel}
      onAcceptClick={handleCreate}
      className={styles['customer-organization-form__modal-dialog']}
    >
      <BodyText>{t('createGroupModalDialog.text')}</BodyText>
      <Gutter />
      <Input
        label={t('createGroupModalDialog.groupNameInputLabel')}
        value={name}
        onChange={(_event, value) => handleNameChange(value)}
        errorText={groupNameError}
        required
        data-testid="groupNameInput"
      />
      <Gutter />
      <Div className={styles['customer-organization-form__create-group-user-roles-container']}>
        <DropdownMultiselect
          label={t('createGroupModalDialog.userRolesLabel')}
          onChange={(_event, option) => handleRolesChange(option.value as UserRole)}
          onClear={() => undefined}
          options={Object.values(UserRole).map(userRole => ({
            value: userRole,
            label: tGlobal(`userRoles.${userRole}`),
            checked: default_user_roles.includes(userRole),
          }))}
        />
      </Div>
    </ModalDialog>
  );
}
